import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";

import { View } from "./View";
import {
  useApiErrors,
  useAuth,
  useHookForm,
  useIdentityMutation,
  useMerchantApiMutation,
  useQueryString,
} from "../../hooks";
import {
  ILoginRequest,
  ILoginResponse,
  IResendConfirmationEmailRequest,
} from "../../interfaces-and-types";
import { getApiErrorsByFieldName, globalHandlerUtils } from "../../utils";
import { accountConfirmationConstants } from "../../constants";

export const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const query = useQueryString();

  const target_page = query.get("target_page");
  const emailQueryString = query.get(
    accountConfirmationConstants.emailQueryStringName
  );

  // if redirected from a different page, fetch email from query string
  useEffect(() => {
    emailQueryString && reset({ ...formValues, email: emailQueryString });
  }, [emailQueryString]);

  const { signIn } = useAuth();

  const {
    // isLoading,
    isSuccess,
    data: apiResponse,
    mutate: login,
  } = useIdentityMutation<ILoginRequest, ILoginResponse>(
    "login"
  ).usePostMutation;

  const {
    isLoading: isLoadingResendVerificationEmail,
    isSuccess: isSuccessResendVerificationEmail,
    mutate: resendConfirmationEmail,
  } = useMerchantApiMutation<IResendConfirmationEmailRequest, null>(
    "resendConfirmationEmail"
  ).usePostMutation;

  const resendVerificationLink = () =>
    resendConfirmationEmail({ email: formEmailValue });

  useEffect(() => {
    if (isSuccess) {
      const isLoginSuccessful = apiResponse.token !== undefined;

      if (isLoginSuccessful) {
        signIn(
          apiResponse.staff_departments,
          apiResponse.token,
          apiResponse.user,
          apiResponse.session_id
        );

        navigate(`${target_page}${location.search}`);
      }
    }
  }, [isSuccess, apiResponse?.token]);

  const initialFormValues: ILoginRequest = {
    email: "",
    password: "",
    remember_me: false,
  };

  const loginSchema: yup.SchemaOf<ILoginRequest> = yup
    .object({
      email: yup.string().email().required(),
      password: yup.string().required(),
      remember_me: yup.boolean().required(),
    })
    .required();

  const handleApiSubmit = (data: ILoginRequest) => {
    login(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, reset, watch } = useHookForm({
    initialFormValues,
    validationSchema: loginSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();
  const formEmailValue = formValues.email;
  const { apiErrors } = useApiErrors();

  const apiErrorMessage = getApiErrorsByFieldName("email", apiErrors);
  const emailNotConfirmed =
    !!formEmailValue &&
    !!apiErrorMessage &&
    apiErrorMessage.toLowerCase().indexOf("confirm") >= 0;

  return (
    <View
      formEmailValue={formEmailValue}
      emailNotConfirmed={emailNotConfirmed}
      resendVerificationLink={resendVerificationLink}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
